import { getStore } from "@store";
import { getCTIDOptions } from "@interfaces/config";
// actions
import {
  showSpinner,
  stopSpinner,
  setToastMessage,
  reportError,
  closeDock,
  clickTab,
  toggleModal,
} from "@actions/global";
import {
  setDTPClientGroups,
  setDTPClients,
  setDTPDetails,
  resetDTP,
  setDTPSessions,
  toggleSummaryModal,
  cancelDTPSession,
} from "@features/dtp/actions";
// utils
import { convertISODateTime } from "@utils/time-helpers";
import { userErrorMessage } from "@utils/custom-errors";
import { getNowISO, roundToInterval } from "@utils/time-helpers";
import { DTP_STATUS } from "./dtpConstants";
// adapters
import DTPAdapter from "@features/dtp/adapters";
// constants
import { API_METHODS } from "@constants";

export const dtpInit = async (store = getStore()) => {
  getCTIDOptions(store);
  getDTPClientGroups(store);
  getDTPSessions(store);
  getDTPHistory(store);
  getDTPTemplates(store);
};

export const getDTPClientGroups = async (store = getStore()) => {
  let clientGroups = await DTPAdapter.getDTPClients();
  if (!Array.isArray(clientGroups)) {
    store.dispatch(reportError(userErrorMessage("Fetch", "DTP Client Groups", clientGroups)));
    return;
  }
  let dtpClients = [];
  clientGroups.forEach((group) => {
    if (group.clienttype_ids && group.clienttype_ids.length > 0) {
      group.clienttype_ids.forEach((client) => {
        let existingClient = dtpClients.find(
          (c) => c.clienttype_id === client.clienttype_id
        );
        if (existingClient) {
          existingClient.client_group.push(group.name);
        } else {
          dtpClients.push({
            ...client,
            client_group: [group.name],
          });
        }
      });
    }
  });
  store.dispatch(setDTPClientGroups(clientGroups));
  store.dispatch(setDTPClients(dtpClients));
};

export const getDTPSessions = async (store = getStore()) => {
  let scheduledResults = await DTPAdapter.getDTPDetails();
  if (!Array.isArray(scheduledResults)) {
    store.dispatch(reportError(userErrorMessage("Fetch", "DTP Sessions", scheduledResults)));
    return;
  }
  let scheduledClients = [];
  scheduledResults.forEach((result) => {
    result?.scheduledClients.forEach((ctid) => {
      if (!scheduledClients.includes(ctid)) {
        scheduledClients.push(ctid);
      }
    });
  });
  store.dispatch(setDTPSessions("active", scheduledResults, scheduledClients));
};

export const getDTPHistory = async (store = getStore()) => {
  let historyResults = await DTPAdapter.getDTPHistory();
  store.dispatch(setDTPSessions("history", historyResults));
};

export const getDTPTemplates = async (store = getStore()) => {
  let templateResults = await DTPAdapter.getDTPTemplates();
  store.dispatch(setDTPSessions("template", templateResults));
};

export const scheduleDTP = async (
  activate = false,
  template = false,
  store = getStore()
) => {
  store.dispatch(showSpinner());
  let storeContents = store.getState();
  let { DTP } = storeContents;
  const editDTP =
    DTP?.id &&
    ((DTP?.status !== DTP_STATUS.INACTIVE && !template) ||
      (template && DTP?.status === DTP_STATUS.INACTIVE));
  const dtpDetails = {
    title: DTP?.title,
    status: template ? "draft" : DTP?.status,
    downtime_pay_pscc: DTP?.downtime_pay_pscc || true,
    downtime_pay_rc: DTP?.downtime_pay_rc || true,
    downtime_message: DTP?.downtime_message,
    notification_message: DTP?.notification_message,
    scheduledClients: DTP?.selectedClients,
    ...(editDTP ? { id: DTP.id } : {}),
    ...(template
      ? {}
      : {
          start_date: DTP?.inputISOStartDate
            ? convertISODateTime(DTP.inputISOStartDate)?.toDBDateTimeString
            : convertISODateTime(roundToInterval(getNowISO()))
                .toDBDateTimeString,
          stop_date: DTP?.inputISOStopDate
            ? convertISODateTime(DTP.inputISOStopDate).toDBDateTimeString
            : null,
        }),
  };
  const dtpSession = await DTPAdapter.createDTPSession(dtpDetails);
  if (typeof dtpSession === "string") {
    store.dispatch(
      reportError(
        userErrorMessage(
          activate ? "Activate" : "Save",
          "DTP Session",
          dtpSession
        )
      )
    );
  } else {
    if (dtpSession && dtpSession?.status === DTP_STATUS.SCHEDULED && activate) {
      await DTPAdapter.activateDTPSession({
        status: dtpSession.status,
        id: dtpSession.id,
      });
    }
    if (template) await getDTPTemplates();
    else await getDTPSessions();
    store.dispatch(
      setToastMessage(
        `DTP has been ${
          dtpSession?.status === DTP_STATUS.SCHEDULED ? "activated" : "updated"
        }.`
      )
    );
    store.dispatch(resetDTP());
    store.dispatch(toggleSummaryModal(false));
    store.dispatch(clickTab(template ? "templates" : "schedule"));
  }
  store.dispatch(stopSpinner());
};

export const cancelDTP = async (dtp, store = getStore()) => {
  store.dispatch(closeDock());
  store.dispatch(showSpinner());
  let dtpSession = {};
  let dtpDetails = {};
  if (dtp.status === DTP_STATUS.SCHEDULED) {
    dtpDetails = {
      id: dtp.id,
      scheduledClients: dtp.selectedClients,
      notification_message: dtp.notification_message,
      downtime_message: dtp.downtime_message,
      downtime_pay_rc: dtp?.downtime_pay_rc || true,
      downtime_pay_pscc: dtp?.downtime_pay_pscc || true,
      start_date: dtp.inputISOStartDate,
      stop_date: dtp.inputISOStopDate,
      status: DTP_STATUS.CANCELLED,
    };
    dtpSession = await DTPAdapter.createDTPSession(dtpDetails);
  } else if (dtp.status === DTP_STATUS.ACTIVE) {
    dtpSession = await DTPAdapter.activateDTPSession({
      status: dtp.status,
      id: dtp.id,
    });
  } else if (dtp.status === DTP_STATUS.DRAFT) {
    dtpSession = await DTPAdapter.deleteTemplate({
      id: dtp.id,
    });
  }
  if (typeof dtpSession === "string") {
    store.dispatch(
      reportError(userErrorMessage("Cancel", "DTP Session", dtpSession))
    );
  } else if (
    dtpSession &&
    (dtpSession.status === DTP_STATUS.CANCELLED ||
      dtpSession.status === DTP_STATUS.COMPLETED ||
      dtpSession.success)
  ) {
    store.dispatch(cancelDTPSession(dtp?.id, dtp?.status));
    store.dispatch(
      setToastMessage(`DTP Session '${dtp.title}' has been deleted.`)
    );
  }
  store.dispatch(stopSpinner());
};

export const setDTPForm = async (dtpSession, store = getStore()) => {
  store.dispatch(showSpinner());
  let storeContents = store.getState();
  const { selectedClients } = dtpSession;
  const isTemplate = dtpSession.status === DTP_STATUS.DRAFT;
  const selectedClientGroups = [
    ...new Set(selectedClients.map((client) => client.client_group).flat()),
  ];
  store.dispatch(
    setDTPDetails({
      ...dtpSession,
      selectedClients: selectedClients.map((client) => client.clienttype_id),
      selectedClientGroups: selectedClientGroups,
      isTemplate: isTemplate,
      status: isTemplate ? DTP_STATUS.INACTIVE : dtpSession.status,
    })
  );
  store.dispatch(clickTab("form"));
  store.dispatch(
    setToastMessage(`DTP '${dtpSession.title}' has been loaded to edit.`)
  );
  store.dispatch(stopSpinner());
};

export const saveGroup = async (
  action = "create",
  groupID = null,
  groupName,
  description,
  groupClients,
  store = getStore()
) => {
  store.dispatch(showSpinner());
  let groupDetails = {
    name: groupName,
  };
  let method = API_METHODS.POST;
  switch (action) {
    case "create":
      groupDetails.description = description;
      groupDetails.clienttype_ids = groupClients;
      break;
    case "edit":
      groupDetails.clienttype_ids = groupClients;
      groupDetails.description = description;
      groupDetails.clients_group_id = groupID;
      method = API_METHODS.PUT;
      break;
    case "delete":
      groupDetails.clients_group_id = groupID;
      method = API_METHODS.DELETE;
      break;
  }
  let result = await DTPAdapter.saveDTPGroup(groupDetails, method);
  if (typeof result === "string" || result?.errors?.length > 0) {
    store.dispatch(
      reportError(
        userErrorMessage(
          "Save",
          "DTP Group",
          result?.errors?.[0]?.message || result
        )
      )
    );
  } else {
    getDTPClientGroups();
    store.dispatch(toggleModal(false));
    store.dispatch(closeDock());
    store.dispatch(
      setToastMessage(
        `DTP Group '${groupName}' has been ${
          action === "delete" ? "deleted" : "saved"
        }.`
      )
    );
  }
  store.dispatch(stopSpinner());
};
